import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PhoneLink } from '../../ui-module/phone-link.js';
import { useTenantConfig } from '../../tenant-config-module/use-tenant-config.js';
import { TechnicalErrorVariant } from '../technical-error-variant.js';

interface TechnicalErrorScreenProps {
  readonly variant: TechnicalErrorVariant;
}

const VARIANT_MESSAGE_KEYS: Readonly<Record<TechnicalErrorVariant, string>> = {
  [TechnicalErrorVariant.Generic]: 'technicalError.generic.message',
  [TechnicalErrorVariant.PinningFailure]: 'technicalError.pinningFailure.message',
};

const ICON_POP = {
  initial: { scale: 0.6, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  transition: { type: 'spring' as const, stiffness: 300, damping: 18 },
};

/**
 * Technical-Error-Screen mit zwei Wortlaut-Varianten (AFO-010):
 *  - `PinningFailure` — handlungsleitender Wortlaut „Link aus Original-SMS".
 *  - `Generic` — generischer Wortlaut.
 *
 * Optik wie der Standard-BusinessError-Layout (X-Icon, rotgetönt), nur ohne
 * Termininfo, weil der Fehler vor der Termin-Auflösung passieren kann.
 * `data-variant` erlaubt deterministisches Targeting.
 */
export function TechnicalErrorScreen(props: TechnicalErrorScreenProps): JSX.Element {
  const { variant } = props;
  const { t } = useTranslation();
  const config = useTenantConfig();
  const messageKey = VARIANT_MESSAGE_KEYS[variant];
  const messageText = t(messageKey, { phoneNumber: config.phoneNumber });

  return (
    <div className="flex flex-col items-center gap-6">
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">
        {t('technicalError.headline')}
      </h1>
      <motion.div
        className="flex h-16 w-16 items-center justify-center rounded-full bg-error/15"
        initial={ICON_POP.initial}
        animate={ICON_POP.animate}
        transition={ICON_POP.transition}
        aria-label={t('technicalError.iconAlt')}
        role="img"
      >
        <X className="h-8 w-8 text-error" strokeWidth={3} />
      </motion.div>
      <p
        role="status"
        aria-live="assertive"
        className="max-w-xs text-center text-sm leading-relaxed text-error"
        data-variant={variant}
      >
        {messageText}
      </p>
      <PhoneLink phoneNumber={config.phoneNumber} label={t('technicalError.callLabel')} />
    </div>
  );
}
